import { Browser } from '@capacitor/browser'
import { isNativeApp } from './platform'

/** Only http/https are allowed targets for user-supplied links (no javascript:, tel:, etc.). */
function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Open an external URL the right way on each platform:
 * - Native (Capacitor iOS/Android): the in-app system browser via the Browser
 *   plugin. A plain `target=_blank`/navigation can no-op or hijack the webview.
 * - Web: a new tab with `noopener,noreferrer`.
 *
 * Returns `false` (and does nothing) for unsafe/non-http(s) URLs.
 */
export function openExternalUrl(url: string): boolean {
  if (!isSafeHttpUrl(url)) return false

  if (isNativeApp()) {
    void Browser.open({ url }).catch(() => {
      // Last-resort fallback if the plugin is unavailable for any reason.
      try {
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch {
        /* ignore */
      }
    })
    return true
  }

  window.open(url, '_blank', 'noopener,noreferrer')
  return true
}

/**
 * Hand off to the platform phone dialer via a tel: URL (e.g. the 000 emergency
 * action). `_self` keeps the WebView/tab in place — both the native shells and
 * browsers route tel: to the dialer without navigating the app away.
 */
export function openTelUrl(phoneNumber: string): void {
  window.open(`tel:${phoneNumber}`, '_self')
}
